/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getNivelesDeEstudios,
  postNivelDeEstudio,
  deleteNivelDeEstudio,
  patchNivelDeEstudio,
  getNivelDeEstudio,
  autorizarNivelDeEstudio,
} from 'api/catalogos/nivelesDeEstudios';
import { checkToken } from 'api/utils';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

export const niveldeestudioPermission = {
  permissionModel: 'niveldeestudio',
  catalogsModels: [],
  tabsModels: [],
};

const presupuestoPlazasmensual = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getNivelesDeEstudios();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getNivelesDeEstudios(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    if (!([1].includes(key))) {
      const match = responseData || data.find((item) => item.id === key);
      const _nivelDeEstudio = await getNivelDeEstudio(match.id);
      setTimeout(() => {
        if (_nivelDeEstudio) {
          form.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(_nivelDeEstudio));
            setDisableFields(_nivelDeEstudio?.estados_globales !== ESTATUS_ACTIVO);
            setVisible(true);
          });
        }
      });
    } else {
      message.warn('Este campo no es editable');
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchNivelDeEstudio(key, values) : postNivelDeEstudio(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteNivelDeEstudio(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Nivel de Estudios ya autorizado');
        return;
      }
      const saved = await autorizarNivelDeEstudio(key);
      if (saved) {
        const _data = await getNivelesDeEstudios();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 200,
    },
    {
      titleText: 'Área',
      dataIndex: 'area',
      key: 'area',
      width: 200,
    },
    {
      titleText: 'Número de Plazas',
      dataIndex: 'numeroPlazas',
      key: 'numeroPlazas',
      width: 150,
    },
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipoNomina',
      key: 'tipoNomina',
      width: 200,
    },
    {
      titleText: 'Fuente Financiamiento',
      dataIndex: 'fuenteFinanciamiento',
      key: 'fuenteFinanciamiento',
      width: 200,
    },
    {
      titleText: '1111 - Dietas',
      dataIndex: 'dietas',
      key: 'dietas',
      width: 150,
    },
    {
      titleText: '1131 - Sueldo Base al Personal Permanente',
      dataIndex: 'sueldoBase',
      key: 'sueldoBase',
      width: 250,
    },
    {
      titleText: '1321 - Primas Vacacionales',
      dataIndex: 'primasVacacionales',
      key: 'primasVacacionales',
      width: 200,
    },
    {
      titleText: '1322 - Gratificaciones de Fin de Año',
      dataIndex: 'gratificaciones',
      key: 'gratificaciones',
      width: 220,
    },
    {
      titleText: 'Total Percepciones',
      dataIndex: 'totalPercepciones',
      key: 'totalPercepciones',
      width: 200,
    },
    {
      titleText: '1411 - Cuotas al IMSS por Enfermedades y Maternidad',
      dataIndex: 'cuotasIMSS',
      key: 'cuotasIMSS',
      width: 280,
    },
    {
      titleText: '1421 - Cuotas para la Vivienda (IPEJAL 3%)',
      dataIndex: 'cuotasIPEJAL',
      key: 'cuotasIPEJAL',
      width: 260,
    },
    {
      titleText: '1431 - Aportaciones al Sistema de Retiro SEDAR',
      dataIndex: 'aportacionesSEDAR',
      key: 'aportacionesSEDAR',
      width: 260,
    },
    {
      titleText: '1432 - Aportaciones al Sistema de Retiro de Pensiones',
      dataIndex: 'aportacionesPensiones',
      key: 'aportacionesPensiones',
      width: 280,
    },
    {
      titleText: 'Total Contribuciones Sobre Nómina',
      dataIndex: 'totalContribuciones',
      key: 'totalContribuciones',
      width: 260,
    },
    {
      titleText: 'Presupuesto del Mes',
      dataIndex: 'presupuestoMes',
      key: 'presupuestoMes',
      width: 220,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          // controls={{
          //   onClickAdd,
          //   onClickEdit: () => onClickEdit(),
          //   onClickDelete,
          // }}
          // customActions={[
          //   {
          //     onClick: () => setVisibleAutorizar(true),
          //     icon: <CheckCircleOutlined />,
          //     text: 'Autorizar',
          //     disabledBySelection: true,
          //     disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
          //     className: 'success',
          //   },
          // ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Nivel de Estudio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Nivel de Estudio ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Nivel de Estudios ${selectedData?.nombre}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Nivel de Estudios ${selectedData?.nombre}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default presupuestoPlazasmensual;
